import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Alert, Button, Card, Form, Modal } from "react-bootstrap";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { colors } from "utils/general";
import { request } from "hooks/useOkoApi/useOkoApi";

/**
 * @typedef {Object} SchoolClassCountResponse
 * @property {string} school_id
 * @property {number | null} class_count
 * @property {number} default_class_count
 * @property {number} [educators_topped_up]
 * @property {number} [classes_created]
 */

const SettingCard = styled(Card)`
  margin-bottom: 24px;
`;

const SettingTitle = styled.h4`
  margin: 0 0 16px;
  font-size: 18px;
  color: ${colors.nightlyWoods900};
`;

const CurrentValueRow = styled.div`
  margin-bottom: 16px;

  label {
    font-size: 12px;
    color: ${colors.nightlyWoods600};
    text-transform: uppercase;
    display: block;
    margin-bottom: 4px;
    font-weight: 600;
  }

  span {
    font-size: 16px;
    font-weight: 600;
    color: ${colors.nightlyWoods900};
  }
`;

const FormRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 12px;
  flex-wrap: wrap;
`;

const InputWrapper = styled.div`
  width: 160px;

  label {
    font-size: 12px;
    color: ${colors.nightlyWoods600};
    display: block;
    margin-bottom: 4px;
  }
`;

const ActionWrapper = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

/**
 * SchoolClassCountSetting component.
 * Allows viewing and updating the per-school class count limit.
 *
 * @param {{ schoolId: string }} props
 * @returns {React.ReactElement}
 */
export const SchoolClassCountSetting = ({ schoolId }) => {
  const [classCount, setClassCount] = useState(/** @type {number | null} */ (null));
  const [defaultClassCount, setDefaultClassCount] = useState(/** @type {number | null} */ (null));
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(/** @type {string | null} */ (null));
  const [successMessage, setSuccessMessage] = useState(/** @type {string | null} */ (null));

  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [pendingValue, setPendingValue] = useState(/** @type {number | null} */ (null));
  const [isResetPending, setIsResetPending] = useState(false);

  const fetchClassCount = useCallback(async () => {
    if (!schoolId) return;
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const data = /** @type {SchoolClassCountResponse} */ (
        await request(`admin/schools/${schoolId}/class-count`, { method: "GET" })
      );
      setClassCount(data.class_count);
      if (data.default_class_count != null) {
        setDefaultClassCount(data.default_class_count);
      }
      setInputValue(data.class_count !== null && data.class_count !== undefined ? String(data.class_count) : "");
    } catch (err) {
      console.error("Failed to fetch class count:", err);
      setError("Failed to load class count setting. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [schoolId]);

  useEffect(() => {
    fetchClassCount();
  }, [fetchClassCount]);

  /**
   * Executes the PUT request to save the class count.
   *
   * @param {number | null} targetCount
   */
  const executeSave = async (targetCount) => {
    if (!schoolId) return;
    setSaving(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const payload = { class_count: targetCount };
      const response = /** @type {SchoolClassCountResponse} */ (
        await request(`admin/schools/${schoolId}/class-count`, {
          method: "PUT",
          payload,
        })
      );
      setClassCount(response.class_count);
      if (response.default_class_count != null) {
        setDefaultClassCount(response.default_class_count);
      }
      setInputValue(response.class_count !== null && response.class_count !== undefined ? String(response.class_count) : "");
      if (targetCount !== null) {
        const created = response.classes_created != null ? response.classes_created : 0;
        const toppedUp = response.educators_topped_up != null ? response.educators_topped_up : 0;
        setSuccessMessage(`Added ${created} classes for ${toppedUp} teachers`);
      } else {
        setSuccessMessage("Reset to default");
      }
      setConfirmModalOpen(false);
      setIsResetPending(false);
      setPendingValue(null);
    } catch (err) {
      console.error("Failed to save class count:", err);
      setError(
        (typeof err === "object" && err !== null && "detail" in err && typeof err.detail === "string" && err.detail) ||
          (err instanceof Error && err.message) ||
          "Failed to update class count setting. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSaveClick = () => {
    const parsed = parseInt(inputValue, 10);
    if (isNaN(parsed) || parsed < 1 || parsed > 20) {
      setError("Class count must be a number between 1 and 20.");
      return;
    }

    const currentEffective = classCount !== null ? classCount : defaultClassCount;
    if (currentEffective !== null && parsed > currentEffective) {
      setIsResetPending(false);
      setPendingValue(parsed);
      setConfirmModalOpen(true);
    } else {
      executeSave(parsed);
    }
  };

  const handleResetDefaultClick = () => {
    const currentEffective = classCount !== null ? classCount : defaultClassCount;
    if (defaultClassCount !== null && currentEffective !== null && defaultClassCount > currentEffective) {
      setIsResetPending(true);
      setPendingValue(null);
      setConfirmModalOpen(true);
    } else {
      executeSave(null);
    }
  };

  const handleConfirmSave = () => {
    executeSave(isResetPending ? null : pendingValue);
  };

  const handleCloseModal = () => {
    setConfirmModalOpen(false);
    setIsResetPending(false);
    setPendingValue(null);
  };

  const parsedInput = parseInt(inputValue, 10);
  const isInputValid = !isNaN(parsedInput) && parsedInput >= 1 && parsedInput <= 20;

  const currentDisplayValue =
    classCount !== null
      ? String(classCount)
      : defaultClassCount !== null
      ? `Default (${defaultClassCount})`
      : "Default";

  const modalTargetNumber = isResetPending ? defaultClassCount : pendingValue;

  return (
    <SettingCard>
      <Card.Body>
        <SettingTitle>Classes Per Teacher</SettingTitle>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert
            variant="success"
            dismissible
            onClose={() => setSuccessMessage(null)}
            data-testid="success-alert"
          >
            {successMessage}
          </Alert>
        )}

        <CurrentValueRow>
          <label>Current Value</label>
          <span data-testid="current-class-count-value">{currentDisplayValue}</span>
        </CurrentValueRow>

        <FormRow>
          <InputWrapper>
            <Form.Label htmlFor="class-count-input">Number of Classes (1-20)</Form.Label>
            <Form.Control
              id="class-count-input"
              type="number"
              min={1}
              max={20}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="1-20"
              disabled={loading || saving || !schoolId}
              data-testid="class-count-input"
            />
          </InputWrapper>

          <ActionWrapper>
            <Button
              variant="primary"
              onClick={handleSaveClick}
              disabled={loading || saving || !isInputValid || !schoolId}
              data-testid="save-class-count-btn"
            >
              {saving ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin className="me-1" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>

            <Button
              variant="outline-secondary"
              onClick={handleResetDefaultClick}
              disabled={loading || saving || classCount === null || !schoolId}
              data-testid="reset-default-btn"
            >
              Reset to default
            </Button>
          </ActionWrapper>
        </FormRow>

        <Modal
          show={confirmModalOpen}
          onHide={handleCloseModal}
          centered
          data-testid="confirm-class-count-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>Confirm Class Count Increase</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {`This adds classes to every current teacher at this school who has fewer than ${modalTargetNumber}. Lowering the number never removes classes.`}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="outline-secondary"
              onClick={handleCloseModal}
              disabled={saving}
              data-testid="cancel-modal-btn"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirmSave}
              disabled={saving}
              data-testid="confirm-modal-btn"
            >
              {saving ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin className="me-1" />
                  Saving...
                </>
              ) : (
                "Confirm"
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </Card.Body>
    </SettingCard>
  );
};

SchoolClassCountSetting.propTypes = {
  schoolId: PropTypes.string.isRequired,
};
